import React from 'react';
import {
  ScrollView,
  View,
  StyleSheet,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useTheme } from '../../hooks/useTheme';
import { RootStackParamList } from '../../types/navigation';
import { Ionicons } from '@expo/vector-icons';

// Import UI components
import Text, { Heading1, Heading2, Body, Caption } from '../common/Text';
import Card from '../common/Card';
import IconButton from '../common/IconButton';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface RouteInfo {
  name: string;
  path: string;
  screen: keyof RootStackParamList;
  params?: Record<string, unknown>;
  description: string;
  category: string;
}

/**
 * Demo component showing all available URL routes
 */
const URLRoutingDemo: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { colors } = useTheme();

  // All available routes with their URL paths
  const routes: RouteInfo[] = [
    // Landing & Auth
    {
      name: 'Landing Page',
      path: '/welcome',
      screen: 'Landing',
      description: 'Welcome/landing page',
      category: 'Public',
    },
    {
      name: 'Sign In',
      path: '/sign-in',
      screen: 'Login',
      description: 'User login page',
      category: 'Auth',
    },
    {
      name: 'Sign Up',
      path: '/sign-up',
      screen: 'Register',
      description: 'User registration page',
      category: 'Auth',
    },
    {
      name: 'Onboarding',
      path: '/onboarding',
      screen: 'Onboarding',
      description: 'User onboarding flow',
      category: 'Auth',
    },

    // Main Features
    {
      name: 'Home',
      path: '/dashboard',
      screen: 'MainTabs',
      description: 'Main dashboard/home page',
      category: 'Core',
    },
    {
      name: 'Smart Wardrobe (Dashboard)',
      path: '/dashboard/smart-wardrobe',
      screen: 'MainTabs',
      params: { section: 'smart-wardrobe' },
      description: 'Manage your digital wardrobe (with sidebar)',
      category: 'Core',
    },
    {
      name: 'Colour Analysis (Dashboard)',
      path: '/dashboard/color-analysis',
      screen: 'MainTabs',
      params: { section: 'color-analysis' },
      description: 'AI-powered colour analysis (with sidebar)',
      category: 'Core',
    },
    {
      name: 'Virtual Drape Test (Dashboard)',
      path: '/dashboard/virtual-drape-test',
      screen: 'MainTabs',
      params: { section: 'virtual-drape-test' },
      description: 'Test colours virtually (with sidebar)',
      category: 'Core',
    },
    {
      name: 'Settings (Dashboard)',
      path: '/dashboard/settings',
      screen: 'MainTabs',
      params: { section: 'settings' },
      description: 'App settings (with sidebar)',
      category: 'Core',
    },
    {
      name: 'Colour Analysis',
      path: '/dashboard/color-analysis',
      screen: 'MainTabs',
      params: { initialTab: 'ColourAnalysis' },
      description: 'AI-powered colour analysis',
      category: 'Core',
    },
    {
      name: 'Smart Wardrobe',
      path: '/dashboard/smart-wardrobe',
      screen: 'MainTabs',
      params: { initialTab: 'SmartWardrobe' },
      description: 'Manage your digital wardrobe',
      category: 'Core',
    },
    {
      name: 'Virtual Drape Test',
      path: '/dashboard/virtual-drape-test',
      screen: 'MainTabs',
      params: { initialTab: 'VirtualDrapeTest' },
      description: 'Test colors virtually',
      category: 'Core',
    },
    {
      name: 'Outfit Suggestions',
      path: '/dashboard/outfit-suggestions',
      screen: 'WearThisToday',
      description: 'Daily outfit recommendations',
      category: 'Core',
    },

    // Mood Boards
    {
      name: 'Mood Boards (Dashboard)',
      path: '/dashboard/mood-boards',
      screen: 'MainTabs',
      params: { section: 'mood-boards' },
      description: 'Browse all mood boards (with sidebar)',
      category: 'Moodboards',
    },
    {
      name: 'Mood Boards (Standalone)',
      path: '/mood-boards',
      screen: 'MoodBoard',
      description: 'Browse all mood boards (standalone)',
      category: 'Moodboards',
    },
    {
      name: 'Create Moodboard',
      path: '/dashboard/mood-board/new',
      screen: 'MoodBoardDetail',
      params: { boardId: 'new' },
      description: 'Create a new moodboard',
      category: 'Moodboards',
    },
    {
      name: 'Edit Moodboard',
      path: '/dashboard/mood-board/123',
      screen: 'MoodBoardDetail',
      params: { boardId: '123' },
      description: 'Edit specific moodboard (ID: 123)',
      category: 'Moodboards',
    },

    // Product Features
    {
      name: 'AI Colour Analysis',
      path: '/dashboard/features/color-analysis',
      screen: 'ColorAnalysisDetail',
      description: 'Learn about colour analysis feature',
      category: 'Features',
    },
    {
      name: 'Virtual Drape Feature',
      path: '/dashboard/features/virtual-drape',
      screen: 'VirtualDrapeDetail',
      description: 'Learn about virtual drape feature',
      category: 'Features',
    },
    {
      name: 'Smart Wardrobe Feature',
      path: '/dashboard/features/smart-wardrobe',
      screen: 'SmartWardrobeDetail',
      description: 'Learn about smart wardrobe feature',
      category: 'Features',
    },
    {
      name: 'Palette Generator',
      path: '/dashboard/features/palette-generator',
      screen: 'PaletteGeneratorDetail',
      description: 'Learn about palette generator',
      category: 'Features',
    },
    {
      name: 'Style Consultation',
      path: '/dashboard/features/style-consultation',
      screen: 'StyleConsultationDetail',
      description: 'Learn about style consultation',
      category: 'Features',
    },
    {
      name: 'Makeup Matching',
      path: '/dashboard/features/makeup-matching',
      screen: 'MakeupMatchingDetail',
      description: 'Learn about makeup colour matching',
      category: 'Features',
    },

    // User & Settings
    {
      name: 'Profile',
      path: '/dashboard/profile',
      screen: 'UserProfile',
      params: { userId: 'current' },
      description: 'User profile page',
      category: 'User',
    },
    {
      name: 'Settings',
      path: '/dashboard/settings',
      screen: 'Settings',
      description: 'App settings',
      category: 'User',
    },
    {
      name: 'Accessibility Settings',
      path: '/dashboard/settings/accessibility',
      screen: 'AccessibilitySettings',
      description: 'Accessibility preferences',
      category: 'User',
    },
    {
      name: 'Language Settings',
      path: '/dashboard/settings/language',
      screen: 'LanguageSelection',
      description: 'Language selection',
      category: 'User',
    },
    {
      name: 'Theme Settings',
      path: '/dashboard/settings/theme',
      screen: 'ThemeSelection',
      description: 'Theme preferences',
      category: 'User',
    },

    // Premium
    {
      name: 'Premium Upgrade',
      path: '/dashboard/premium',
      screen: 'PremiumPaywall',
      description: 'Premium subscription page',
      category: 'Premium',
    },
  ];

  const navigateToScreen = (route: RouteInfo) => {
    try {
      if (route.params) {
        // @ts-ignore - Dynamic navigation with params
        navigation.navigate(route.screen, route.params);
      } else {
        // @ts-ignore - Dynamic navigation without params
        navigation.navigate(route.screen);
      }
    } catch {
      Alert.alert('Navigation Error', `Could not navigate to ${route.name}`);
    }
  };

  const copyToClipboard = (text: string) => {
    // For web, use the Clipboard API
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      Alert.alert('Copied!', 'URL copied to clipboard');
    } else {
      Alert.alert('URL', text);
    }
  };

  const groupedRoutes = routes.reduce(
    (acc, route) => {
      if (!acc[route.category]) {
        acc[route.category] = [];
      }
      acc[route.category].push(route);
      return acc;
    },
    {} as Record<string, RouteInfo[]>
  );

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <View style={styles.content}>
        <Heading1 style={styles.title}>URL Routing Demo</Heading1>
        <Body style={styles.subtitle}>
          All available routes in your app - click to navigate or copy URL
        </Body>

        {Object.entries(groupedRoutes).map(([category, categoryRoutes]) => (
          <Card key={category} variant="elevated" padding="lg" margin="md">
            <Heading2 style={styles.categoryTitle}>{category}</Heading2>
            <Caption style={styles.categoryDescription}>
              {categoryRoutes.length} route
              {categoryRoutes.length !== 1 ? 's' : ''} available
            </Caption>

            {categoryRoutes.map((route, index) => (
              <Card key={index} variant="outlined" padding="md" margin="sm">
                <View style={styles.routeHeader}>
                  <View style={styles.routeInfo}>
                    <Text weight="semibold" size="md">
                      {route.name}
                    </Text>
                    <Caption style={styles.routeDescription}>
                      {route.description}
                    </Caption>
                    <TouchableOpacity
                      onPress={() => copyToClipboard(route.path)}
                      style={styles.urlContainer}
                    >
                      <Text variant="primary" size="sm" style={styles.urlText}>
                        {route.path}
                      </Text>
                      <Ionicons
                        name="copy-outline"
                        size={16}
                        color={colors.primary}
                        style={styles.copyIcon}
                      />
                    </TouchableOpacity>
                  </View>

                  <IconButton
                    icon="arrow-forward-outline"
                    iconPosition="only"
                    variant="outline"
                    size="sm"
                    onPress={() => navigateToScreen(route)}
                  />
                </View>
              </Card>
            ))}
          </Card>
        ))}

        {/* Dynamic Route Examples */}
        <Card variant="filled" padding="lg" margin="md">
          <Heading2>Dynamic Route Examples</Heading2>
          <Body style={styles.dynamicDescription}>
            These routes accept dynamic parameters:
          </Body>

          <View style={styles.dynamicExample}>
            <Text weight="semibold">Mood Board by ID:</Text>
            <Text variant="primary" size="sm">
              /dashboard/mood-board/[boardId]
            </Text>
            <Caption>
              Replace [boardId] with actual board ID (e.g., 123, abc, new)
            </Caption>
          </View>

          <View style={styles.dynamicExample}>
            <Text weight="semibold">Color Analysis Result:</Text>
            <Text variant="primary" size="sm">
              /dashboard/color-analysis/[profileId]
            </Text>
            <Caption>Replace [profileId] with actual profile ID</Caption>
          </View>
        </Card>

        {/* Instructions */}
        <Card variant="outlined" padding="lg" margin="md">
          <Heading2>How to Use URL Routing</Heading2>
          <View style={styles.instruction}>
            <Text weight="semibold">1. Direct Navigation:</Text>
            <Caption>Type any URL directly in your browser address bar</Caption>
          </View>
          <View style={styles.instruction}>
            <Text weight="semibold">2. Bookmarks:</Text>
            <Caption>Bookmark any page and return to it later</Caption>
          </View>
          <View style={styles.instruction}>
            <Text weight="semibold">3. Sharing:</Text>
            <Caption>Share specific page URLs with others</Caption>
          </View>
          <View style={styles.instruction}>
            <Text weight="semibold">4. Browser Back/Forward:</Text>
            <Caption>Use browser navigation buttons normally</Caption>
          </View>
        </Card>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  title: {
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    textAlign: 'center',
    marginBottom: 24,
  },
  categoryTitle: {
    marginBottom: 4,
  },
  categoryDescription: {
    marginBottom: 16,
  },
  routeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  routeInfo: {
    flex: 1,
    marginRight: 12,
  },
  routeDescription: {
    marginTop: 4,
    marginBottom: 8,
  },
  urlContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: '#F8F9FA',
    borderRadius: 6,
    marginTop: 4,
  },
  urlText: {
    flex: 1,
    fontFamily: 'monospace',
  },
  copyIcon: {
    marginLeft: 8,
  },
  dynamicDescription: {
    marginBottom: 16,
  },
  dynamicExample: {
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  instruction: {
    marginBottom: 12,
  },
});

export default URLRoutingDemo;
